import React, { useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as MuiIcons from "@mui/icons-material";
import WidgetEditor from "./WidgetEditor";
import { ICON_OPTIONS, getDefaultOptions } from "./widgetFormSchemas";

// ============================================================================
// SORTABLE WIDGET WRAPPER
// ============================================================================

function SortableWidget({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 0,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {React.cloneElement(children, { dragHandleProps: listeners })}
    </div>
  );
}

// ============================================================================
// SECTION EDITOR
// ============================================================================

export default function SectionEditor({
  section,
  sectionIndex,
  onChange,
  onRemove,
  dataSources,
  dragHandleProps,
  onSaveAsTemplate,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [detailActionEnabled, setDetailActionEnabled] = useState(
    !!section.detailAction
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Get MUI icon component
  const IconComp = MuiIcons[section.icon] || MuiIcons.Settings;

  // ============================================================================
  // HANDLERS - SECTION
  // ============================================================================

  const handleFieldChange = useCallback(
    (field, value) => {
      onChange({ ...section, [field]: value });
    },
    [section, onChange]
  );

  const handleDetailActionToggle = useCallback(() => {
    const newEnabled = !detailActionEnabled;
    setDetailActionEnabled(newEnabled);

    if (newEnabled) {
      onChange({
        ...section,
        detailAction: { type: "history", popupType: "", targetIds: "" },
      });
    } else {
      const newSection = { ...section };
      delete newSection.detailAction;
      onChange(newSection);
    }
  }, [detailActionEnabled, section, onChange]);

  const handleDetailActionChange = useCallback(
    (field, value) => {
      onChange({
        ...section,
        detailAction: {
          ...section.detailAction,
          [field]: value,
        },
      });
    },
    [section, onChange]
  );

  // ============================================================================
  // HANDLERS - WIDGETS
  // ============================================================================

  const handleWidgetChange = useCallback(
    (widgetIndex, updatedWidget) => {
      const newWidgets = [...(section.widgets || [])];
      newWidgets[widgetIndex] = updatedWidget;
      onChange({ ...section, widgets: newWidgets });
    },
    [section, onChange]
  );

  const handleWidgetRemove = useCallback(
    (widgetIndex) => {
      const newWidgets = (section.widgets || []).filter(
        (_, idx) => idx !== widgetIndex
      );
      onChange({ ...section, widgets: newWidgets });
    },
    [section, onChange]
  );

  const handleWidgetAdd = useCallback(() => {
    const newWidget = {
      type: "info_list",
      options: getDefaultOptions("info_list"),
      items: [],
    };
    const newWidgets = [...(section.widgets || []), newWidget];
    onChange({ ...section, widgets: newWidgets });
  }, [section, onChange]);

  const handleWidgetReorder = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const widgets = section.widgets || [];
      const oldIndex = widgets.findIndex(
        (_, idx) => `${section.id}-widget-${idx}` === active.id
      );
      const newIndex = widgets.findIndex(
        (_, idx) => `${section.id}-widget-${idx}` === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(widgets, oldIndex, newIndex);
        onChange({ ...section, widgets: reordered });
      }
    },
    [section, onChange]
  );

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const commonInputClasses =
    "bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500/50";

  const widgets = section.widgets || [];
  const widgetIds = widgets.map((_, idx) => `${section.id}-widget-${idx}`);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="bg-slate-800/20 border border-slate-700/30 rounded-2xl overflow-hidden">
      {/* HEADER */}
      <div
        className="flex items-center gap-3 px-4 py-3 bg-slate-800/40 rounded-t-2xl cursor-pointer hover:bg-slate-700/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Drag Handle */}
        <div
          {...dragHandleProps}
          className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300"
          onClick={(e) => e.stopPropagation()}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8h16M4 16h16"
            />
          </svg>
        </div>

        {/* Section Icon */}
        <IconComp sx={{ fontSize: 18 }} className="text-indigo-400" />

        {/* Section Title */}
        <div className="flex-1">
          <span className="text-base font-bold text-slate-200">
            {section.title || "제목 없음"}
          </span>
        </div>

        {/* Widget Count Badge */}
        <div className="px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-400 text-xs font-semibold">
          {widgets.length} 위젯
        </div>

        {/* Save as Template Button */}
        {onSaveAsTemplate && (
          <button
            className="text-slate-500 hover:text-emerald-400 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onSaveAsTemplate(section);
            }}
            title="템플릿으로 저장"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
              />
            </svg>
          </button>
        )}

        {/* Collapse/Expand Toggle */}
        <button className="text-slate-400 hover:text-slate-200">
          <svg
            className={`w-5 h-5 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Delete Button */}
        <button
          className="text-slate-500 hover:text-red-400 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      {/* BODY */}
      {isExpanded && (
        <div className="px-4 py-4 space-y-4">
          {/* Section Fields */}
          <div className="grid grid-cols-2 gap-3">
            {/* Section ID */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                섹션 ID <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                className={commonInputClasses}
                value={section.id || ""}
                onChange={(e) => handleFieldChange("id", e.target.value)}
                placeholder="예: general_info"
              />
            </div>

            {/* Section Title */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                섹션 제목 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                className={commonInputClasses}
                value={section.title || ""}
                onChange={(e) => handleFieldChange("title", e.target.value)}
                placeholder="예: 기본 정보"
              />
            </div>

            {/* Icon Picker */}
            <div className="space-y-1 col-span-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                아이콘
              </label>
              <div className="flex items-center gap-2">
                <select
                  className={commonInputClasses}
                  value={section.icon || ""}
                  onChange={(e) => handleFieldChange("icon", e.target.value)}
                >
                  <option value="">선택...</option>
                  {ICON_OPTIONS.map((icon) => (
                    <option key={icon} value={icon}>
                      {icon}
                    </option>
                  ))}
                </select>
                {section.icon && (
                  <div className="flex items-center justify-center w-10 h-10 bg-slate-800/60 rounded-lg">
                    <IconComp sx={{ fontSize: 20 }} className="text-indigo-400" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Detail Action */}
          <div className="space-y-3 pt-3 border-t border-slate-700/50">
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                상세 액션
              </label>
              <button
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  detailActionEnabled ? "bg-indigo-500" : "bg-slate-600"
                }`}
                onClick={handleDetailActionToggle}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    detailActionEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {detailActionEnabled && section.detailAction && (
              <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-indigo-500/30">
                {/* Type */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                    타입
                  </label>
                  <select
                    className={commonInputClasses}
                    value={section.detailAction.type || ""}
                    onChange={(e) =>
                      handleDetailActionChange("type", e.target.value)
                    }
                  >
                    <option value="">선택...</option>
                    <option value="history">History</option>
                    <option value="expand">Expand</option>
                    <option value="update">Update (값 수정)</option>
                  </select>
                </div>

                {/* Popup Type (only for history) */}
                {section.detailAction.type === "history" && (
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                      팝업 타입
                    </label>
                    <input
                      type="text"
                      className={commonInputClasses}
                      value={section.detailAction.popupType || ""}
                      onChange={(e) =>
                        handleDetailActionChange("popupType", e.target.value)
                      }
                      placeholder="예: riskStatus"
                    />
                  </div>
                )}

                {/* Target IDs (only for expand) */}
                {section.detailAction.type === "expand" && (() => {
                  // Collect all widgets in this section that have an ID set
                  const widgetsWithId = (section.widgets || [])
                    .map((w, idx) => ({ ...w, _idx: idx }))
                    .filter(w => w.id);
                  
                  // Parse current targetIds into a Set
                  const currentTargets = new Set(
                    (section.detailAction.targetIds || "")
                      .split(",")
                      .map(s => s.trim())
                      .filter(Boolean)
                  );

                  const toggleTarget = (widgetId) => {
                    const next = new Set(currentTargets);
                    if (next.has(widgetId)) {
                      next.delete(widgetId);
                    } else {
                      next.add(widgetId);
                    }
                    handleDetailActionChange("targetIds", [...next].join(", "));
                  };

                  return (
                    <div className="space-y-2 col-span-2">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                        대상 위젯 선택
                      </label>
                      {widgetsWithId.length === 0 ? (
                        <p className="text-xs text-slate-500 py-2">
                          ID가 설정된 위젯이 없습니다. 위젯 편집에서 ID를 먼저 설정하세요.
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {widgetsWithId.map((w) => (
                            <label
                              key={w.id}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${
                                currentTargets.has(w.id)
                                  ? "bg-indigo-500/20 border border-indigo-500/40"
                                  : "bg-slate-800/40 border border-slate-700/30 hover:bg-slate-700/40"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={currentTargets.has(w.id)}
                                onChange={() => toggleTarget(w.id)}
                                className="accent-indigo-500"
                              />
                              <code className="text-xs text-indigo-400 font-mono">{w.id}</code>
                              <span className="text-[10px] text-slate-500">
                                ({w.type}{w.hidden ? ", 숨김" : ""})
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                      {/* Fallback manual input for IDs not yet created */}
                      <div className="mt-2">
                        <input
                          type="text"
                          className={commonInputClasses}
                          value={section.detailAction.targetIds || ""}
                          onChange={(e) =>
                            handleDetailActionChange("targetIds", e.target.value)
                          }
                          placeholder="수동 입력: widget1, widget2"
                        />
                        <p className="text-xs text-slate-500 mt-0.5">
                          직접 입력도 가능합니다 (쉼표로 구분)
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Update action fields */}
                {section.detailAction.type === "update" && (
                  <>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                        라벨
                      </label>
                      <input
                        type="text"
                        className={commonInputClasses}
                        value={section.detailAction.label || ""}
                        onChange={(e) =>
                          handleDetailActionChange("label", e.target.value)
                        }
                        placeholder="예: 교체주기"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                        데이터 소스
                      </label>
                      <input
                        type="text"
                        className={commonInputClasses}
                        value={section.detailAction.source_key || ""}
                        onChange={(e) =>
                          handleDetailActionChange("source_key", e.target.value)
                        }
                        placeholder="예: tip_replacement_cycle"
                      />
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        column 타입 데이터 소스만 수정 가능
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                        컬럼명
                      </label>
                      <input
                        type="text"
                        className={commonInputClasses}
                        value={section.detailAction.dataKey || ""}
                        onChange={(e) =>
                          handleDetailActionChange("dataKey", e.target.value)
                        }
                        placeholder="비워두면 source_key 값 직접 사용"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                        단위
                      </label>
                      <input
                        type="text"
                        className={commonInputClasses}
                        value={section.detailAction.unit || ""}
                        onChange={(e) =>
                          handleDetailActionChange("unit", e.target.value)
                        }
                        placeholder="예: 회, %, mm"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">
                        입력 타입
                      </label>
                      <select
                        className={commonInputClasses}
                        value={section.detailAction.inputType || "text"}
                        onChange={(e) =>
                          handleDetailActionChange("inputType", e.target.value)
                        }
                      >
                        <option value="text">텍스트</option>
                        <option value="number">숫자</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Widgets List */}
          <div className="space-y-3 pt-3 border-t border-slate-700/50">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-slate-300">
                위젯 ({widgets.length})
              </h4>
              <button
                className="text-xs px-3 py-1.5 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors font-semibold"
                onClick={handleWidgetAdd}
              >
                + 위젯 추가
              </button>
            </div>

            {widgets.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">
                위젯이 없습니다. 추가 버튼을 클릭하세요.
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleWidgetReorder}
              >
                <SortableContext
                  items={widgetIds}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {widgets.map((widget, widgetIdx) => (
                      <SortableWidget
                        key={widgetIdx}
                        id={widgetIds[widgetIdx]}
                      >
                        <WidgetEditor
                          widget={widget}
                          widgetIndex={widgetIdx}
                          onChange={(updatedWidget) =>
                            handleWidgetChange(widgetIdx, updatedWidget)
                          }
                          onRemove={() => handleWidgetRemove(widgetIdx)}
                          dataSources={dataSources}
                        />
                      </SortableWidget>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
